export const AGENT_TYPE = {
  AI: 'AI_AGENT',
  HUMAN: 'HUMAN_AGENT',
}

export const DEFAULT_AGENT_TYPE = AGENT_TYPE.AI

export const USER_ROLE = {
  ADMIN: 'ADMIN',
  AGENT: 'AGENT',
}

export const DEFAULT_USER_ROLE = USER_ROLE.ADMIN

export function normalizeUserRole(value) {
  const key = String(value ?? '').trim().toUpperCase()
  if (key === USER_ROLE.AGENT) return USER_ROLE.AGENT
  return USER_ROLE.ADMIN
}

export const USER_ROLE_OPTIONS = [
  {
    value: USER_ROLE.ADMIN,
    title: 'Admin',
    subtitle: 'Full portal access — email & phone',
    icon: 'mdi-shield-account-outline',
    tone: 'ai',
  },
  {
    value: USER_ROLE.AGENT,
    title: 'Agent',
    subtitle: 'AI agent or human team member',
    icon: 'mdi-headset',
    tone: 'human',
  },
]

export function resolveCreateOrgUserRole(agentType) {
  return isAiAgent(agentType) ? USER_ROLE.AGENT : USER_ROLE.ADMIN
}

export const AGENT_TYPE_OPTIONS = [
  {
    value: AGENT_TYPE.AI,
    title: 'AI agent',
    subtitle: 'Virtual team member — name only',
    icon: 'mdi-robot-outline',
    tone: 'ai',
  },
  {
    value: AGENT_TYPE.HUMAN,
    title: 'Human agent',
    subtitle: 'Portal access with email & phone',
    icon: 'mdi-account-outline',
    tone: 'human',
  },
]

export function normalizeAgentType(value) {
  const key = String(value ?? '').trim().toUpperCase()
  if (key === AGENT_TYPE.AI) return AGENT_TYPE.AI
  if (key === AGENT_TYPE.HUMAN) return AGENT_TYPE.HUMAN
  return DEFAULT_AGENT_TYPE
}

export function isAiAgent(agentType) {
  return normalizeAgentType(agentType) === AGENT_TYPE.AI
}

export function isHumanAgent(agentType) {
  return normalizeAgentType(agentType) === AGENT_TYPE.HUMAN
}

export function resolveListAgentType(user = {}) {
  const raw = user.agentType ?? user.agent_type
  if (raw != null && String(raw).trim()) {
    return normalizeAgentType(raw)
  }

  const email = String(user.email ?? user.user_email_id ?? '').trim()
  const hasEmail = email && email !== '—'
  const mobileNumber = String(user.mobileNumber ?? user.mobile_number ?? '').trim()
  const contactDisplay = String(user.contactDisplay ?? '').trim()
  const hasContact =
    Boolean(mobileNumber) ||
    (Boolean(contactDisplay) && contactDisplay !== '—')

  if (hasEmail || hasContact) return AGENT_TYPE.HUMAN
  return AGENT_TYPE.AI
}

const EMAIL_PATTERN = /.+@.+\..+/

export function validateCreateOrgUserForm(form = {}) {
  const agentType = normalizeAgentType(form.agent_type)
  const errors = {}

  const userName = String(form.user_name ?? '').trim()
  if (!userName) errors.user_name = 'Name is required'

  if (isHumanAgent(agentType)) {
    const userEmailId = String(form.user_email_id ?? '').trim()
    if (!userEmailId) errors.user_email_id = 'Email is required'
    else if (!EMAIL_PATTERN.test(userEmailId)) {
      errors.user_email_id = 'Enter a valid email'
    }

    const countryCode = String(form.country_code ?? '').trim()
    if (!countryCode) errors.country_code = 'Country code is required'

    const mobileNumber = String(form.mobile_number ?? '').replace(/\D/g, '')
    if (!mobileNumber) errors.mobile_number = 'Mobile number is required'
    else if (mobileNumber.length !== 10) {
      errors.mobile_number = 'Enter a valid 10-digit number'
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
    agentType,
  }
}

export function agentTypeLabel(agentType) {
  if (isAiAgent(agentType)) return 'AI agent'
  if (normalizeAgentType(agentType) === AGENT_TYPE.HUMAN) return 'Human'
  return '—'
}

export function buildCreateOrgUserInput(form = {}) {
  const agentType = normalizeAgentType(form.agent_type)
  const explicitRole = String(form.user_role ?? '').trim().toUpperCase()
  const userRole =
    explicitRole === USER_ROLE.ADMIN || explicitRole === USER_ROLE.AGENT
      ? explicitRole
      : resolveCreateOrgUserRole(agentType)

  const input = {
    user_name: String(form.user_name ?? '').trim(),
    user_role: userRole,
    agent_type: agentType,
  }

  if (isHumanAgent(agentType)) {
    input.user_email_id = String(form.user_email_id ?? '').trim()
    input.country_code = String(form.country_code ?? '').trim()
    input.mobile_number = String(form.mobile_number ?? '').replace(/\D/g, '')
  }

  if (isAiAgent(agentType)) {
    const agentGender = String(form.agent_gender ?? '').trim()
    const agentLanguage = String(form.agent_language ?? '').trim()
    if (agentGender) input.agent_gender = agentGender
    if (agentLanguage) input.agent_language = agentLanguage
  }

  return input
}

export function buildUpdateOrgUserInput(form = {}) {
  const input = {
    user_id: String(form.user_id ?? form.userId ?? '').trim(),
  }

  const userName = String(form.user_name ?? '').trim()
  if (userName) input.user_name = userName

  const agentType =
    form.agent_type != null && String(form.agent_type).trim()
      ? normalizeAgentType(form.agent_type)
      : normalizeAgentType(form.current_agent_type)

  if (form.agent_type != null && String(form.agent_type).trim()) {
    input.agent_type = agentType
  }

  if (!isAiAgent(agentType)) {
    const countryCode = String(form.country_code ?? '').trim()
    const mobileNumber = String(form.mobile_number ?? '').replace(/\D/g, '')

    if (countryCode) input.country_code = countryCode
    if (mobileNumber) input.mobile_number = mobileNumber
  } else {
    if (form.agent_gender !== undefined) input.agent_gender = String(form.agent_gender ?? '').trim() || null
    if (form.agent_language !== undefined) input.agent_language = String(form.agent_language ?? '').trim() || null
  }

  return input
}
